/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import {Graphic} from '../Graphic';
import {geohashForLocation} from 'geofire-common';
import {TextOverlayView, TextOverlayViewOptions} from '../TextOverlayView/TextOverlayView';
import {SubscriptionEvent, SubscriptionManager} from '@/lib/filters/subscriptions';
import * as libColors from '@/lib/colors';
import type {LinkedLabelLink} from './linkedLabel';

const createLabelGraphic = (feature: LabelGraphicOptions) => new LabelGraphic(feature);

/** Labels offer the parcel/overlay swatches plus white (absent from overlayColorPalette). */
const availableColors = [...libColors.overlayColorPalette, libColors.white];

type LabelGraphicOptions = TextOverlayViewOptions & {
  linkedLabel?: LinkedLabelLink;
};
class LabelGraphic extends Graphic {
  private textOverlay: TextOverlayView;
  private linkedLabel: LinkedLabelLink | null;
  private subscriptions: SubscriptionManager;
  private handlers: Map<string, google.maps.MapsEventListener>;
  private clickable: boolean = false;

  constructor(featureOptions: LabelGraphicOptions) {
    super(featureOptions);
    const {linkedLabel, ...textOverlayOptions} = featureOptions;
    this.linkedLabel = linkedLabel ?? null;
    this.textOverlay = new TextOverlayView({...textOverlayOptions, uuid: this.uuid, readOnly: !!linkedLabel});
    this.subscriptions = new SubscriptionManager();
    this.handlers = new Map();
  }

  addHandler(eventType: string, handlerName: string, handler: Function) {
    super.addHandler(eventType, handlerName, handler);
  }

  addLocalHandler(eventType: string, handlerName: string, handler: Function) {
    const id = eventType + handlerName;
    if (this.handlers.has(id)) {
      this.removeLocalHandler(eventType, handlerName);
    }
    const listener = this.textOverlay.addListener(eventType, handler);
    this.handlers.set(id, listener);
  }

  removeLocalHandler(eventType: string, handlerName: string) {
    const id = eventType + handlerName;
    const listener = this.handlers.get(id);
    if (listener) {
      listener.remove();
      this.handlers.delete(id);
    }
  }

  setClickable(clickable: boolean) {
    this.clickable = clickable;
    this.textOverlay.setClickable(clickable);
  }

  isClickable() {
    return this.clickable;
  }

  setMap = (map: google.maps.Map) => {
    this.textOverlay.setMap(map);
  };

  subscribe(eventType: string, handler: (arg0: SubscriptionEvent) => void) {
    return this.subscriptions.addSubscription(eventType, handler);
  }

  setPointerEvents(enabled: boolean) {
    this.textOverlay.setPointerEvents(enabled);
  }

  setHighlight(highlight: boolean) {
    this.textOverlay.setHighlighted(highlight);
  }

  async setEditing(isEditing: boolean) {
    if (!isEditing) {
      await this.textOverlay.setEditing(false);
      const hasText = this.textOverlay.text.trim().length > 0;
      const payload = hasText ? this.toJSON() : null;
      this.subscriptions.publish('stopped_editing', payload);
      return false;
    }
    this.textOverlay.addListener('overlay_view_changed', () => {
      this.subscriptions.publish('graphic_changed');
    });
    this.subscriptions.publish('open_info_window', {
      toolbar: this.getToolbar(),
      featureType: 'label',
    });
    this.textOverlay.setEditing(true);
    return false;
  }

  private toolbarAction(options: Partial<TextOverlayViewOptions>) {
    this.textOverlay.setOptions(options);
    this.subscriptions.publish('graphic_changed');
  }

  getToolbar() {
    return {
      toolbarOptions: [
        [ // box group
          {
            icon: 'Fill',
            action: 'colorPicker',
            name: 'Box Border and Fill',
            pickers: [
              {
                title: 'Box Border',
                getColor: () => this.textOverlay.borderColor,
                setColor: (color: string) => this.toolbarAction({borderColor: color}),
                availableColors,
              },
              {
                title: 'Box Fill',
                getColor: () => this.textOverlay.backgroundColor,
                setColor: (color: string) => this.toolbarAction({backgroundColor: color}),
                availableColors,
              },
            ],
          },
        ],
        [ // font style group
          { // font color
            icon: 'Font',
            action: 'colorPicker',
            name: 'Font Color and Border',
            pickers: [
              {
                title: 'Font Border',
                getColor: () => this.textOverlay.textBorderColor,
                setColor: (color: string) => this.toolbarAction({textBorderColor: color}),
                availableColors,
              },
              {
                title: 'Font Color',
                getColor: () => this.textOverlay.textColor,
                setColor: (color: string) => this.toolbarAction({textColor: color}),
                availableColors,
              },
            ],
          },
          { // bold
            name: 'bold',
            icon: 'Bold',
            action: 'boldText',
            callback: () => this.toolbarAction({bold: !this.textOverlay.bold}),
            isSelected: () => this.textOverlay.bold,
          },
          { // italic
            name: 'italic',
            icon: 'Italic',
            action: 'italicizeText',
            callback: () => this.toolbarAction({italic: !this.textOverlay.italic}),
            isSelected: () => this.textOverlay.italic,
          },
          { // underline
            name: 'underline',
            icon: 'Underline',
            action: 'underlineText',
            callback: () => this.toolbarAction({underline: !this.textOverlay.underline}),
            isSelected: () => this.textOverlay.underline,
          },
        ], [ // alignment group
          {
            icon: 'CenterAlign',
            action: 'openAlignmentDropdown',
            dropdownOptions: [
              {
                name: 'left',
                icon: 'LeftAlign',
                action: 'alignTextLeft',
                textAlign: 'left',
                callback: () => this.toolbarAction({alignment: 'left'}),
                isSelected: () => this.textOverlay.alignment === 'left',
              },
              {
                name: 'center',
                icon: 'CenterAlign',
                action: 'alignTextCenter',
                textAlign: 'center',
                callback: () => this.toolbarAction({alignment: 'center'}),
                isSelected: () => this.textOverlay.alignment === 'center',
              },
              {
                name: 'right',
                icon: 'RightAlign',
                action: 'alignTextRight',
                textAlign: 'right',
                callback: () => this.toolbarAction({alignment: 'right'}),
                isSelected: () => this.textOverlay.alignment === 'right',
              },
            ],
          },
        ],
      ],
    };
  }

  get text(): string {
    return this.textOverlay.text;
  }

  setLinkedFields(text: string, fieldLabels: string[]) {
    if (!this.linkedLabel) throw new Error('Only linked labels have linked fields');
    this.linkedLabel = {...this.linkedLabel, fieldLabels};
    this.textOverlay.setTextAndFit(text);
    this.subscriptions.publish('graphic_changed');
  }

  setZIndex(zIndex: number) {
    this.textOverlay.setOptions({zIndex});
  }

  toJSON() {
    const superJson = super.toJSON();
    const bounds = this.textOverlay.bounds;
    if (!bounds) throw new Error('LabelGraphic bounds are not defined');
    const graphicData: Record<string, unknown> = {
      featureType: 'label',
      schemaVersion: 2,
      bounds,
      center: this.textOverlay.center,
      text: this.textOverlay.text,
      rotation: this.textOverlay.rotation,
      backgroundColor: this.textOverlay.backgroundColor,
      borderColor: this.textOverlay.borderColor,
      textColor: this.textOverlay.textColor,
      textBorderColor: this.textOverlay.textBorderColor,
      bold: this.textOverlay.bold,
      italic: this.textOverlay.italic,
      underline: this.textOverlay.underline,
      alignment: this.textOverlay.alignment,
      scale: this.textOverlay.scale,
    };
    // Firestore rejects undefined, so the key is only written for linked labels
    if (this.linkedLabel) {
      graphicData.linkedLabel = this.linkedLabel;
    }
    return {
      ...superJson,
      bounds,
      geohash: geohashForLocation([(bounds.north + bounds.south) / 2, (bounds.east + bounds.west) / 2]),
      graphicData,
    };
  }
}

export {
  createLabelGraphic,
  LabelGraphic,
};
